const READY_TIMEOUT = 20000;
const FRONT_THETA_TOLERANCE = 0.12;
const FRONT_PHI_TOLERANCE = 0.14;
const DESKTOP_INITIAL_CAMERA_ORBIT = '0deg 90deg 215%';
const MOBILE_INITIAL_CAMERA_ORBIT = '0deg 90deg 132%';
const INITIAL_FIELD_OF_VIEW = '23deg';
const IDLE_RESET_DELAY = 8000;
const RESET_SETTLE_DELAY = 1200;
const ZOOM_IN_RADIUS_RATIO = 0.96;

class ZoProduct360Viewer extends HTMLElement {
  #model;
  #status;
  #idleTimer;
  #readyTimer;
  #restoreTimer;
  #resetSettleTimer;
  #initialRadius;
  #isAutoResetting = false;

  connectedCallback() {
    this.#model = this.querySelector('[data-viewer-model]');
    this.#status = this.querySelector('[data-viewer-status]');

    if (!this.#model) {
      this.#showError();
      return;
    }

    this.handleLoad = this.handleLoad.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleCameraChange = this.handleCameraChange.bind(this);
    this.handleInteractionStart = this.handleInteractionStart.bind(this);

    this.#model.addEventListener('load', this.handleLoad);
    this.#model.addEventListener('error', this.handleError);
    this.#model.addEventListener('camera-change', this.handleCameraChange);
    this.#model.addEventListener('pointerdown', this.handleInteractionStart);
    this.#model.addEventListener('wheel', this.handleInteractionStart);

    this.#model.setAttribute('camera-orbit', this.#initialCameraOrbit());

    this.#readyTimer = window.setTimeout(() => this.#showError(), READY_TIMEOUT);
  }

  disconnectedCallback() {
    this.#model?.removeEventListener('load', this.handleLoad);
    this.#model?.removeEventListener('error', this.handleError);
    this.#model?.removeEventListener('camera-change', this.handleCameraChange);
    this.#model?.removeEventListener('pointerdown', this.handleInteractionStart);
    this.#model?.removeEventListener('wheel', this.handleInteractionStart);
    window.clearTimeout(this.#idleTimer);
    window.clearTimeout(this.#readyTimer);
    window.clearTimeout(this.#restoreTimer);
    window.clearTimeout(this.#resetSettleTimer);
  }

  handleLoad() {
    window.clearTimeout(this.#readyTimer);
    this.#initialRadius = this.#model?.getCameraOrbit?.().radius;
    this.dataset.viewerReady = 'true';
    this.dataset.viewerError = 'false';
    this.dataset.annotationsVisible = 'true';
    if (this.#status) this.#status.textContent = 'Interactive 360 degree product view ready.';
  }

  handleError() {
    this.#showError();
  }

  handleInteractionStart() {
    this.#isAutoResetting = false;
    this.dataset.annotationsVisible = 'false';
    window.clearTimeout(this.#idleTimer);
    window.clearTimeout(this.#restoreTimer);
    window.clearTimeout(this.#resetSettleTimer);
  }

  handleCameraChange() {
    if (this.#isAutoResetting) return;
    this.dataset.annotationsVisible = 'false';
    this.#queueAnnotationRestore();
    this.#queueIdleReset();
  }

  #canShowAnnotations() {
    const orbit = this.#model?.getCameraOrbit?.();
    if (!orbit) return false;

    const wrappedTheta = Math.atan2(Math.sin(orbit.theta), Math.cos(orbit.theta));
    const isFrontView = (
      Math.abs(wrappedTheta) <= FRONT_THETA_TOLERANCE &&
      Math.abs(orbit.phi - Math.PI / 2) <= FRONT_PHI_TOLERANCE
    );
    const isZoomedIn = Number.isFinite(this.#initialRadius) &&
      orbit.radius < this.#initialRadius * ZOOM_IN_RADIUS_RATIO;

    return isFrontView && !isZoomedIn;
  }

  #queueAnnotationRestore() {
    window.clearTimeout(this.#restoreTimer);
    const delay = Number.parseInt(this.dataset.frontIdleDelay || '700', 10);

    this.#restoreTimer = window.setTimeout(() => {
      this.dataset.annotationsVisible = this.#canShowAnnotations() ? 'true' : 'false';
    }, Number.isFinite(delay) ? delay : 700);
  }

  #queueIdleReset() {
    window.clearTimeout(this.#idleTimer);
    this.#idleTimer = window.setTimeout(() => {
      window.clearTimeout(this.#restoreTimer);
      window.clearTimeout(this.#resetSettleTimer);

      this.#isAutoResetting = true;
      this.dataset.annotationsVisible = 'false';
      this.#model?.setAttribute('camera-orbit', this.#initialCameraOrbit());
      this.#model?.setAttribute('field-of-view', INITIAL_FIELD_OF_VIEW);

      this.#resetSettleTimer = window.setTimeout(() => {
        this.#isAutoResetting = false;
        this.dataset.annotationsVisible = this.#canShowAnnotations() ? 'true' : 'false';
      }, RESET_SETTLE_DELAY);
    }, IDLE_RESET_DELAY);
  }

  #showError() {
    window.clearTimeout(this.#readyTimer);
    this.dataset.viewerReady = 'false';
    this.dataset.viewerError = 'true';
    this.dataset.annotationsVisible = 'false';
    if (this.#status) this.#status.textContent = 'Interactive view unavailable. Product image shown.';
  }

  #initialCameraOrbit() {
    return window.matchMedia('(max-width: 749px)').matches
      ? MOBILE_INITIAL_CAMERA_ORBIT
      : DESKTOP_INITIAL_CAMERA_ORBIT;
  }
}

if (!customElements.get('zo-product-360-viewer')) {
  customElements.define('zo-product-360-viewer', ZoProduct360Viewer);
}
